<template>
  <div class="w-full flex flex-col flex-grow relative h-full">
    <!-- Mobile: horizontal pill tabs -->
    <nav v-if="isMobile" class="relative flex flex-col justify-start items-center overflow-hidden w-full flex-none pt-2 pb-1">
      <div class="flex flex-row justify-start w-full gap-2 overflow-x-auto">
        <label
          v-for="(tab, idx) in tabs"
          :key="tab.title"
          :ref="el => tabsRef[idx] = el"
          :class="[
            'w-fit flex-shrink-0 font-normal cursor-pointer px-4 py-1.5 rounded-full border',
            'transition-all duration-300 ease-out flex items-center gap-2',
            selectedTab.title === tab.title
              ? 'bg-primary-500 text-white font-medium border-transparent shadow-sm'
              : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50',
            idx === tabs.length - 1 ? 'ml-2' : '',
            idx === 0 ? 'mr-2' : ''
          ]"
          @click="selectTab(tab)"
        >
          <span>{{ tab.title }}</span>
          <span
            v-if="tab.count != null && tab.count > 0"
            :class="selectedTab.title === tab.title ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'"
            class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold leading-none"
          >{{ tab.count }}</span>
          <span
            v-if="tab.badge && tab.badge > 0"
            class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none"
          >{{ tab.badge }}</span>
          <img
            v-if="tab.isLocked"
            src="../../../../../assets/img/admin/premium.png"
            alt="diamond logo"
            class="w-4 h-4 ml-2">
        </label>
      </div>
      <p v-if="selectedTab.description" class="text-xs text-surface-500 w-full px-2 md:pt-2 pt-4 text-center md:text-start">{{ selectedTab.description }}</p>
    </nav>

    <!-- Desktop: selected tab name as header -->
    <div v-else class="flex-none pe-2 ps-8 pt-4 pb-2 border-gray-100">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-medium text-gray-800 flex items-center gap-2.5">
          {{ selectedTab.title }}
          <span
            v-if="selectedTab.count != null && selectedTab.count > 0"
            class="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-sm font-semibold bg-gray-100 text-gray-500 leading-none"
          >{{ selectedTab.count }}</span>
        </h2>
        <slot :name="`header-end-${selectedTab.index}`" />
      </div>
      <p v-if="selectedTab.description" class="text-sm text-surface-500 mt-1">{{ selectedTab.description }}</p>
    </div>

    <!-- Content -->
    <div :class="['w-full flex-1 min-h-0', isMobile ? 'pt-4' : 'pt-4', contentClass]">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { ITab } from './tab.interface'
import { useResponsive } from '@app/composables/responsive';

interface Props {
  contentClass?: string
}

withDefaults(defineProps<Props>(), {
  contentClass: ''
})

const emit = defineEmits(['tabSelect'])
const route = useRoute()
const { isMobile } = useResponsive();

const slotsRef = defineSlots()
const tabs = computed<Array<ITab>>(() => {
  const vnodes = slotsRef.default?.() || [];
  return vnodes.filter(vnode => vnode?.props?.title).map((tab, index) => <ITab>{ ...tab?.props, index });
})
const selectedTab = ref<ITab>(tabs.value[0] || { title: '', index: 0 })
const tabsRef = ref<Array<any>>([])
const scrollToTab = (index: number) => {
  const tabEl = tabsRef.value[index]
  if (tabEl) {
    tabEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }
}

const selectTab = (tab: ITab) => {
  selectedTab.value = tab
  emit('tabSelect', tab.index)
  nextTick(() => scrollToTab(tab.index))
}

const applyTabFromQuery = () => {
  const tabParam = route.query.tab
  if (tabParam !== undefined) {
    const idx = Number(tabParam)
    if (!isNaN(idx) && tabs.value[idx]) {
      selectTab(tabs.value[idx])
    }
  }
}

// Sync when route query changes (e.g. sidebar click while already on page)
watch(() => route.query.tab, applyTabFromQuery)

onMounted(() => {
  applyTabFromQuery()
})

provide('selectedTab', selectedTab)
</script>

